import React, { useEffect, useState } from "react";

import { Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/hooks/use-toast";
import { useProducts } from "~/hooks/useProducts";
import { supabase } from "~/integrations/supabase/client";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

interface ModifyDispatchOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  order: SalesRequest;
  currentItems: SalesRequestItem[];
  onOrderModified: () => void;
}

interface OrderItem {
  product_id: string;
  quantity: number;
  unit_price: number;
}

export const ModifyDispatchOrderDialog: React.FC<
  ModifyDispatchOrderDialogProps
> = ({ isOpen, onClose, order, currentItems, onOrderModified }) => {
  const { products } = useProducts();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reason, setReason] = useState("");
  const [newItems, setNewItems] = useState<OrderItem[]>([]);

  // Initialize with current items when dialog opens
  useEffect(() => {
    if (isOpen && currentItems.length > 0) {
      setNewItems(
        currentItems.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
          unit_price: item.unitPrice,
        }))
      );
    }
  }, [isOpen, currentItems]);

  // Calculate total
  const total = newItems.reduce(
    (sum, item) => sum + item.quantity * item.unit_price,
    0
  );

  // Add new item
  const addItem = () => {
    setNewItems([...newItems, { product_id: "", quantity: 1, unit_price: 0 }]);
  };

  // Remove item
  const removeItem = (index: number) => {
    setNewItems(newItems.filter((_, i) => i !== index));
  };

  // Update item
  const updateItem = (index: number, field: keyof OrderItem, value: any) => {
    const updated = [...newItems];
    updated[index] = { ...updated[index], [field]: value };
    setNewItems(updated);
  };

  // Submit modification
  const handleSubmit = async () => {
    if (newItems.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos un producto",
        variant: "destructive",
      });
      return;
    }

    const invalidItems = newItems.filter(
      (item) => !item.product_id || item.quantity <= 0 || item.unit_price <= 0
    );

    if (invalidItems.length > 0) {
      toast({
        title: "Error",
        description: "Todos los productos deben tener valores válidos",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        "modify-order-dispatch",
        {
          body: {
            sales_request_id: order.id,
            new_items: newItems,
            reason:
              reason ||
              "Modificación de productos en orden con guía de despacho",
          },
        }
      );

      if (error) {
        console.error("Error modifying order:", error);
        toast({
          title: "Error",
          description: "No se pudo modificar la orden",
          variant: "destructive",
        });
        return;
      }

      if (!data.success) {
        toast({
          title: "Error",
          description: data.error || "No se pudo modificar la orden",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "¡Orden modificada!",
        description:
          "La orden ha sido modificada y se ha generado una nueva guía de despacho",
        variant: "default",
      });

      onOrderModified();
      onClose();
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "Error inesperado al modificar la orden",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReason("");
      setNewItems([]);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modificar Orden con Guía de Despacho</DialogTitle>
          <DialogDescription>
            Modifica los productos de esta orden. Se crearán guías de anulación
            para las actuales y una nueva guía de despacho con los productos
            actualizados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Info */}
          <div className="rounded-lg border bg-blue-50 p-4">
            <h3 className="font-medium text-blue-900">
              Información de la Orden
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                <strong>ID:</strong> {order.id.slice(0, 8)}...
              </p>
              <p>
                <strong>Estado:</strong> {order.status}
              </p>
              <p>
                <strong>Total Actual:</strong>{" "}
                {formatCurrency(order.totalAmount)}
              </p>
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Motivo de la modificación (opcional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Cliente solicitó cambio de cantidad..."
              disabled={isSubmitting}
            />
          </div>

          {/* Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Productos</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addItem}
                disabled={isSubmitting}
              >
                <Plus className="mr-2 h-4 w-4" />
                Agregar Producto
              </Button>
            </div>

            {newItems.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No hay productos. Haz clic en "Agregar Producto" para comenzar.
              </div>
            ) : (
              <div className="space-y-3">
                {newItems.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-12 items-end gap-3 rounded-lg border p-3"
                  >
                    {/* Product Selection */}
                    <div className="col-span-5">
                      <Label className="text-xs">Producto</Label>
                      <Select
                        value={item.product_id}
                        onValueChange={(value) =>
                          updateItem(index, "product_id", value)
                        }
                        disabled={isSubmitting}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar producto" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.short_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Quantity */}
                    <div className="col-span-2">
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(
                            index,
                            "quantity",
                            parseInt(e.target.value) || 1
                          )
                        }
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Unit Price */}
                    <div className="col-span-2">
                      <Label className="text-xs">Precio Unitario</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) =>
                          updateItem(
                            index,
                            "unit_price",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Total */}
                    <div className="col-span-2">
                      <Label className="text-xs">Total</Label>
                      <div className="text-sm font-medium">
                        {formatCurrency(item.quantity * item.unit_price)}
                      </div>
                    </div>

                    {/* Remove Button */}
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        disabled={isSubmitting}
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Total Summary */}
          {newItems.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between text-lg font-semibold">
                <span>Total Nuevo:</span>
                <span>{formatCurrency(total)}</span>
              </div>
              {total !== order.totalAmount && (
                <div className="mt-1 text-sm text-gray-600">
                  <span>Cambio: </span>
                  <span
                    className={
                      total > order.totalAmount
                        ? "text-red-600"
                        : "text-green-600"
                    }
                  >
                    {total > order.totalAmount ? "+" : ""}
                    {formatCurrency(total - order.totalAmount)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 border-t pt-6">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || newItems.length === 0}
            >
              {isSubmitting ? "Modificando..." : "Modificar Orden"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
